// App.js
import "./App.css";
import { Suspense, useEffect, useContext, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

// utils
import { mobileScreenSize } from "./utils/commonFunctions";
import { MyContext } from "./context/MyContext";
import routes from "./routes";
import { useUserQuery } from "./views/tutorV2/hooks/queries/useUserQuery";

// components
import Loading from "./components/Loading";
import Header from "./components/Header";
import Nav from "./components/Nav";
import HeaderMo from "./components/HeaderMo";
import PopupModal from "./components/Popup";
import TicketAlert from "./components/TicketAlert";

// view
import NotFound from "./views/common/NotFound";
import HeaderBanner from "./components/HeaderBanner";
import Alert from "./components/Alert";
import Shared from "./components/Shared";
import ConfirmModal from "./components/overlay/ConfirmModal";
import DeviceLimitModal from "./components/overlay/DeviceLimitModal";
import { useUIStore } from "./views/tutorV2/store";
import { useAuth } from "./views/tutorV2/hooks/useAuth";
import GuideView from "./views/guide/GuideView";

function App() {
  const location = useLocation();
  const queryClient = useQueryClient();

  // useUser 훅으로 user 상태 관리 (tanstack-query 기반)
  const { refetchUser } = useUserQuery();

  // Context에서 상태와 함수 가져오기
  const {
    history,
    setHistory,
    pageName,
    setPageName,
    list,
    setParamId,
    isMobile,
    openNav,
    setIsProfile,
    setIsSticky,
    navRef,
    alertModal,
    alertSetting,
    isAppMode,
  } = useContext(MyContext);

  const { setPageName: setPageNameV2 } = useUIStore();

  const POPUP_LINK =
    "https://dl.dongascience.com/sub/event/view/uFkgjGS1/?utm_source=air&utm_medium=ownedmedia&utm_campaign=2607_air_app_popup";
  const POPUP_IMG_SRC = "https://img.dongascience.com/air/air_popup_kt.png";

  const handleScroll = () => {
    // 현재 스크롤 위치가 100px 이상일 때
    if (window.scrollY > 100) {
      setIsSticky(true);
    } else {
      setIsSticky(false);
    }
  };

  useEffect(() => {
    // 스크롤 이벤트 리스너 추가
    window.addEventListener("scroll", handleScroll);
    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    // 튜터 대화 기록 추가시 남은 튜터 수 체크를 위해 API 호출
    if (pageName === "tutor") {
      refetchUser();
    }
  }, [history.length, pageName, refetchUser]);

  const isShareRoute = location.pathname.startsWith("/share");
  const isEduMainRoute = location.pathname === "/guide/edumain";
  const v2Pages = [
    "/tutor",
    "/my-air",
    "/socrates",
    "/result",
    "/my-activity",
    "/company-info",
    "/order-list",
    "/guide",
  ];
  const isRandingPage = location.pathname === "/";
  const isV2 =
    isRandingPage || v2Pages.find((path) => location.pathname.startsWith(path));
  const showLegacyHeader = !isShareRoute && !isV2;

  // 배너 표시 페이지: /, /tutor, /socrates, /result
  const bannerPages = ["/tutor", "/socrates", "/result"];
  const showBanner =
    isRandingPage ||
    bannerPages.some((path) => location.pathname.startsWith(path));

  // 모바일에서 서비스 헤더(HeaderMo)를 숨길 페이지
  const hideServiceHeaderMoPages = ["/my-air"];
  const shouldHideServiceHeaderMo =
    isMobile &&
    hideServiceHeaderMoPages.some((path) => location.pathname.startsWith(path));

  // PC에서 자체 헤더를 사용하는 페이지 (Header 숨김)
  const selfHeaderPages = ["/my-activity", "/company-info", "/order-list"];
  const hasSelfHeader = selfHeaderPages.some((path) =>
    location.pathname.startsWith(path)
  );

  useEffect(() => {
    // V2 페이지, EduMain 페이지는 레거시 높이 설정 적용하지 않음
    if (isV2 || isEduMainRoute) {
      return;
    }

    const mainElement = document.querySelector(".md-wrp") as HTMLElement;
    if (isShareRoute) {
      if (mainElement) {
        mainElement.style.height = "100vh";
      }
      return;
    }

    let vh = mobileScreenSize();

    const updateVh = () => {
      // visualViewport 사용 (Android 웹뷰에서 키보드 상태 반영)
      const height = window.visualViewport?.height ?? window.innerHeight;
      let vh = height * 0.01;

      document.documentElement.style.setProperty("--vh", `${vh}px`);

      const htmlElement = document.querySelector("html");
      const bodyElement = document.querySelector("body");
      const rootElement = document.getElementById("root");

      const navElement = document.querySelector(".nav-wrp") as HTMLElement;

      const lyWrpElement = document.querySelector(".ly-wrp") as HTMLElement;

      if (rootElement && bodyElement && htmlElement) {
        htmlElement.style.height = isMobile
          ? "calc(var(--vh, 1vh) * 100)"
          : "100vh";
        bodyElement.style.height = isMobile
          ? "calc(var(--vh, 1vh) * 100)"
          : "100vh";
        rootElement.style.height = isMobile
          ? "calc(var(--vh, 1vh) * 100)"
          : "100vh";
        if (lyWrpElement) {
          lyWrpElement.style.height = isMobile
            ? "calc(var(--vh, 1vh) * 100)"
            : "100vh";
        }
        navElement.style.height = isMobile
          ? "calc(var(--vh, 1vh) * 100 )"
          : // : "100%"
            // : "calc(100% - 127px)";
            "-webkit-fill-available";
        navElement.style.position = isMobile ? "absolute" : "";
        mainElement.style.height = isMobile
          ? "calc(var(--vh, 1vh) * 7)"
          : "calc(100% - 210px)";
        // mainElement.style.height = isMobile ? 'calc(1vh * 7)' : 'calc(100% - 110px)';
        // mainElement.style.height = isMobile ? 'calc(var(--vh, 1vh) * 100 - 147px)' : 'calc(100% - 110px)';

        // mainElement.style.height = isMobile ? "auto" : "calc(100% - 127px)";
        // mainElement.style.height = "calc(100% - 144px)";
      }
    };

    // 초기 실행
    updateVh();

    // visualViewport 이벤트 리스너 추가 (Android 웹뷰 키보드 감지)
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", updateVh);
    }

    // resize 이벤트 리스너 추가
    window.addEventListener("resize", updateVh);

    // cleanup 함수
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", updateVh);
      }
      window.removeEventListener("resize", updateVh);
    };
  }, [isMobile, isV2, isShareRoute, isEduMainRoute]);

  // location 변화시 localStorage 저장 값 업데이트
  useEffect(() => {
    // location 변경 시 user 정보 refetch (로그인 후 리다이렉트 감지)
    queryClient.invalidateQueries({ queryKey: ["user"] });

    const params = location.pathname.split("/")[2];
    let path = location.pathname.split("/")[1];
    let target = location.state?.from_chat;
    let is_writed = location.state?.is_writed;
    let is_completed = location.state?.is_completed;
    let his_cnt = location?.state?.his_cnt;
    let show_result = location?.state?.show_result;

    if (is_writed === undefined) {
      list?.forEach((item) => {
        if (item.id === Number(params)) {
          is_writed = item.is_writed;
        }
      });
    }
    if (is_completed === undefined) {
      list?.forEach((item) => {
        if (item.id === Number(params)) {
          is_completed = item.is_completed;
        }
      });
    }
    if (target === undefined) {
      list?.forEach((item) => {
        if (item.id === Number(params)) {
          target = item.from_chat;
        }
      });
    }
    if (his_cnt === undefined) {
      list?.forEach((item) => {
        if (item.id === Number(params)) {
          his_cnt = item.total_his_count;
        }
      });
    }

    const url = {
      pathname: path,
      params: params,
      state: {
        from_chat: target,
        is_writed: target === "storage" ? false : is_writed,
        is_completed: target === "storage" ? false : is_completed,
        his_cnt: his_cnt,
        show_result: show_result,
      },
    };

    if (path) {
      window.localStorage.setItem("url", JSON.stringify(url));
    }
    const isSetting = path.includes("settings") || path.includes("login");
    if (path === "result") return;
    setIsProfile(isSetting);
    if (path === "") {
      path = "tutor";
    }
    setPageName(isSetting ? params : path);
    setPageNameV2(isSetting ? params : path);
    if (params) {
      setParamId(params);
    } else {
      setParamId(null);
    }
  }, [location]);

  useEffect(() => {
    // 페이지명 변경시 (튜터, 소크라테스, 마이페이지) 대화 이력 초기화
    if (pageName) {
      setHistory([]);
    }
  }, [pageName]);

  const [modalVisible, setModalVisible] = useState(true);
  const [serviceModalVisible, setServiceModalVisible] = useState(true);

  const closeModal = (shouldClose) => {
    if (shouldClose === true) {
      // 오늘 하루 닫기 또는 같은 날짜인 경우
      setModalVisible(false);
    } else if (shouldClose === false) {
      // 다른 날짜이므로 팝업을 계속 표시
      setModalVisible(true);
    } else {
      // 일반 닫기 버튼 클릭 (매개변수 없음)
      setModalVisible(false);
    }
  };

  const closeServiceModal = (shouldClose) => {
    if (shouldClose === true) {
      // 오늘 하루 닫기 또는 같은 날짜인 경우
      setServiceModalVisible(false);
    } else if (shouldClose === false) {
      // 다른 날짜이므로 팝업을 계속 표시
      setServiceModalVisible(true);
    } else {
      // 일반 닫기 버튼 클릭 (매개변수 없음)
      setServiceModalVisible(false);
    }
  };

  const mnWrapClass = `mn-wrp ${isShareRoute ? "mn-wrp-share" : ""} ${
    isV2 ? "h-full" : ""
  }`;

  const { shareListModalOpen, setShareListModalOpen, logoutConfirmOpen, setLogoutConfirmOpen } = useUIStore();
  const { logout } = useAuth();

  return (
    <Suspense fallback={<Loading />}>
      {isEduMainRoute ? (
        <Routes>
          <Route path="/guide/:guideId" Component={GuideView} />
        </Routes>
      ) : (
        // .ly-wrp 높이는 common.scss에서 100dvh로 보정 (#11328). h-screen만 두면 100vh가 적용돼
        // 모바일/태블릿에서 #root(100dvh)+overflow:hidden보다 커져 하단 입력칸이 잘림.
        <div className="ly-wrp flex flex-col h-screen" ref={navRef}>
          {/* 팝업이 어플에서만 나오게 수정 */}
          {isAppMode && (
            <PopupModal
              imgSrc={POPUP_IMG_SRC}
              link={POPUP_LINK}
              visible={modalVisible}
              closable={true}
              maskClosable={true}
              onClose={closeModal}
              isMobile={isMobile}
              className={""}
            />
          )}
          {/* 서비스 종료 팝업 숨김 처리
          <PopupModal
            imgSrc={`https://img.dongascience.com/air/${
              isAppMode
                ? "popup_air_service_will_stop_app.jpg"
                : "popup_air_service_will_stop_web.jpg"
            }`}
            link={"#"}
            visible={serviceModalVisible}
            closable={true}
            maskClosable={true}
            onClose={closeServiceModal}
            isMobile={isMobile}
            className={""}
            cookieKey="serviceModal"
          />
          */}

          {isV2 ? (
            <>
              {/* 배너 */}
              {showBanner && <HeaderBanner />}
              {/* V2 페이지에서 HeaderMo/Header 렌더링 */}
              {/* 모바일: hideServiceHeaderMoPages 페이지에서 숨김 */}
              {/* PC: 항상 노출 */}
              {!isShareRoute &&
                (isMobile ? !shouldHideServiceHeaderMo && <HeaderMo /> : <Header />)}
              {/* // V2 페이지 전용 레이아웃: box-border 명시적 사용 */}
              <div className="v2-layout flex-1 min-h-0 overflow-hidden">
                <Routes>
                  {routes.map((item, idx) => {
                    return (
                      <Route
                        key={item.name}
                        path={item.isParams ? `${item.path}/:id?` : item.path}
                        Component={item.element}
                      />
                    );
                  })}
                  <Route path="*" Component={NotFound} />
                </Routes>
                <TicketAlert />
                {shareListModalOpen && (
                  <Shared onClose={() => setShareListModalOpen(false)} />
                )}
              </div>
            </>
          ) : (
            // 레거시 페이지 레이아웃
            <>
              {!isShareRoute && (isMobile ? <HeaderMo /> : <Header />)}
              <div className="flx-box md-wrp flex-1 flex-col md:flex-row">
                {showLegacyHeader && <Nav />}
                <div className={mnWrapClass} style={openNav ? null : { flex: 1 }}>
                  <Routes>
                    {routes.map((item, idx) => {
                      return (
                        <Route
                          key={item.name}
                          path={item.isParams ? `${item.path}/:id?` : item.path}
                          Component={item.element}
                        />
                      );
                    })}
                    <Route path="*" Component={NotFound} />
                  </Routes>
                  {/* <Footer /> */}
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {alertModal && <Alert settings={alertSetting} />}
      <ConfirmModal
        open={logoutConfirmOpen}
        onOpenChange={setLogoutConfirmOpen}
        title="로그아웃"
        message="정말 로그아웃 하실건가요?"
        confirmText="로그아웃 하기"
        onConfirm={logout}
      />
      {/* 기기 등록 초과 안내 (튜터/소크라테스 공용) */}
      <DeviceLimitModal />
    </Suspense>
  );
}

export default App;
